import React, { useCallback, useEffect, useState } from 'react';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import SearchIcon from '@material-ui/icons/Search';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';

import SimpleDialog from '../Dialog/Dialog';
import { Text } from '../Inputs/Text/Text';
import { CheckBox } from '../Inputs/CheckBox/CheckBox';
import { ButtonPrimary, ButtonDefault } from '../Button/Button';
import { ToastCF } from '../Toast/Toast';

import CandidatoService from '../../services/Candidato';
import ProvaService from '../../services/Prova';
import { descreverErro } from '../../services/API';
import { cpfMask } from '../../helper/MaskHelper';
import { verifyPagingAndSorting, ASC } from '../Table/Utils';

const CAMPOS_BUSCA = [
    { value: 'nome', label: 'Nome' },
    { value: 'cpf', label: 'CPF' },
    { value: 'codigoProva', label: 'Código de Prova' },
    { value: 'codigo', label: 'Código de Inscrição' },
];

const ROTULO_CAMPO = {
    nome: 'Nome do Candidato',
    cpf: 'CPF do Candidato',
    codigoProva: 'Código de Prova',
    codigo: 'Código de Inscrição',
};

const TAMANHO_PAGINA = 20;
const FILTRO_REGIAO = 'regiaoProvaConfirmado';
const FILTRO_STATUS = 'status';
const STATUS_INSCRICAO_FINALIZADA = 'FINALIZADO';

const useStyles = makeStyles(theme => ({
    contexto: {
        display: 'block',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: 4,
        border: '1px solid #d6e0ff',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        backgroundColor: '#f2f6ff',
        fontWeight: 600,
        fontSize: 14,
    },
    radiosInline: {
        display: 'flex',
        alignItems: 'center',
    },
    radiosInlineLabel: {
        marginRight: theme.spacing(2),
        whiteSpace: 'nowrap',
    },
    buscaLinha: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    buscaCampo: {
        flex: 1,
    },
    resultados: {
        marginTop: theme.spacing(1),
        maxHeight: 320,
        overflowY: 'auto',
        border: '1px solid #eeeeee',
        borderRadius: 4,
    },
    resultado: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 1),
        borderBottom: '1px solid #f2f2f2',
    },
    resultadoInfo: {
        minWidth: 0,
    },
    resultadoSecundario: {
        fontSize: 11,
        color: '#999',
    },
    vazio: {
        padding: theme.spacing(3),
        textAlign: 'center',
    },
    rodape: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
    },
    confirmacao: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
}));

const regiaoDaProva = prova => {
    if (!prova) return '--';

    return `${prova.regiao || '--'}${prova.regiaoGrupo ? ` / ${prova.regiaoGrupo}` : ''}`;
};

const regiaoDoCandidato = candidato =>
    (candidato.localProvaFinal && candidato.localProvaFinal.regiao && candidato.localProvaFinal.regiao.id) ||
    (candidato.endereco && candidato.endereco.municipio && candidato.endereco.municipio.regiao && candidato.endereco.municipio.regiao.id) ||
    '--';

const DialogVincularCandidato = ({ open, prova, onClose, onVinculado }) => {
    const classes = useStyles();

    const [etapa, setEtapa] = useState('selecao');
    const [campoBusca, setCampoBusca] = useState('nome');
    const [termo, setTermo] = useState('');
    const [somenteMesmaRegiao, setSomenteMesmaRegiao] = useState(false);
    const [resultados, setResultados] = useState([]);
    const [pagina, setPagina] = useState(0);
    const [totalPaginas, setTotalPaginas] = useState(0);
    const [totalElementos, setTotalElementos] = useState(0);
    const [selecionado, setSelecionado] = useState(null);
    const [buscou, setBuscou] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [carregandoMais, setCarregandoMais] = useState(false);
    const [vinculando, setVinculando] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', type: 'error' });

    const notificar = useCallback((message, type = 'error') => setToast({ open: true, message, type }), []);

    useEffect(() => {
        if (!open) return;

        setEtapa('selecao');
        setCampoBusca('nome');
        setTermo('');
        setSomenteMesmaRegiao(false);
        setResultados([]);
        setPagina(0);
        setTotalPaginas(0);
        setTotalElementos(0);
        setSelecionado(null);
        setBuscou(false);
        setCarregando(false);
        setCarregandoMais(false);
        setVinculando(false);
    }, [open, prova]);

    const executarBusca = useCallback(async (paginaAlvo, anexar = false) => {
        const termoLimpo = termo.trim();

        if (!termoLimpo) return;

        const filtros = [{ field: campoBusca, value: termoLimpo }, { field: FILTRO_STATUS, value: STATUS_INSCRICAO_FINALIZADA }];

        if (somenteMesmaRegiao && prova && prova.regiao)
            filtros.push({ field: FILTRO_REGIAO, value: prova.regiao });

        verifyPagingAndSorting({ filters: filtros, filtered: true, page: paginaAlvo, size: TAMANHO_PAGINA, sort: [{ orderBy: 'nome', order: ASC }] });

        if (anexar) {
            setCarregandoMais(true);
        } else {
            setCarregando(true);
        }

        try {
            const resposta = await CandidatoService.filters(filtros);
            const dados = resposta.data || {};
            const conteudo = Array.isArray(dados.content) ? dados.content : [];

            setResultados(anteriores => anexar ? [...anteriores, ...conteudo] : conteudo);
            setPagina(typeof dados.number === 'number' ? dados.number : paginaAlvo);
            setTotalPaginas(typeof dados.totalPages === 'number' ? dados.totalPages : 0);
            setTotalElementos(typeof dados.totalElements === 'number' ? dados.totalElements : conteudo.length);
            setBuscou(true);
        } catch (error) {
            if (!anexar) {
                setResultados([]);
                setBuscou(true);
            }

            notificar(descreverErro(error));
        } finally {
            setCarregando(false);
            setCarregandoMais(false);
        }
    }, [campoBusca, termo, somenteMesmaRegiao, prova, notificar]);

    const buscar = () => {
        setSelecionado(null);
        executarBusca(0, false);
    };

    const carregarMais = () => executarBusca(pagina + 1, true);

    const handleClose = () => {
        if (vinculando) return;

        if (onClose) onClose();
    };

    const confirmar = async () => {
        if (!selecionado || vinculando) return;

        setVinculando(true);

        try {
            const resposta = await ProvaService.vincularCandidato(prova.id, { candidatoId: selecionado.id });

            if (onVinculado) onVinculado(resposta.data, selecionado);
        } catch (error) {
            notificar(descreverErro(error));
            setEtapa('selecao');
        } finally {
            setVinculando(false);
        }
    };

    const acoesSelecao = (
        <>
            <ButtonPrimary
                name="Vincular"
                startIcon={<CheckOutlinedIcon />}
                onClick={() => setEtapa('confirmacao')}
                disabled={!selecionado || carregando}
                style={{ margin: 4 }}
            />
            <ButtonDefault
                name="Cancelar"
                startIcon={<ClearOutlinedIcon />}
                onClick={handleClose}
                disabled={vinculando}
                style={{ margin: 4 }}
            />
        </>
    );

    const acoesConfirmacao = (
        <>
            <ButtonPrimary
                name="Confirmar Vínculo"
                startIcon={<CheckOutlinedIcon />}
                onClick={confirmar}
                disabled={vinculando}
                style={{ margin: 4 }}
            />
            <ButtonDefault
                name="Voltar"
                startIcon={<ClearOutlinedIcon />}
                onClick={() => setEtapa('selecao')}
                disabled={vinculando}
                style={{ margin: 4 }}
            />
        </>
    );

    const temMais = pagina + 1 < totalPaginas;

    return (
        <>
            <ToastCF
                open={toast.open}
                message={toast.message}
                type={toast.type}
                handleClose={() => setToast(anterior => ({ ...anterior, open: false }))}
            />

            <SimpleDialog
                openDialog={Boolean(open)}
                title={etapa === 'confirmacao' ? 'Confirmar Vínculo' : 'Vincular Candidato'}
                closeDialog={handleClose}
                actions={etapa === 'confirmacao' ? acoesConfirmacao : acoesSelecao}
                fullWidth={true}
                maxWidth="md"
            >
                {etapa === 'confirmacao' && selecionado && prova &&
                    <div className={classes.confirmacao}>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            Confirma o vínculo do candidato abaixo à prova <b>{prova.codigo}</b> (Região {regiaoDaProva(prova)})?
                        </Typography>
                        <div className={classes.contexto}>
                            <Typography variant="body1" style={{ marginBottom: 8 }}><b>{selecionado.nomeExibicao}</b></Typography>
                            {selecionado.nomeSocial &&
                                <Typography variant="body2" color="textSecondary">Nome: {selecionado.nome}</Typography>
                            }
                            <Typography variant="body2" color="textSecondary">CPF: {selecionado.cpf ? cpfMask(selecionado.cpf) : '--'}</Typography>
                            <Typography variant="body2" color="textSecondary">Região: {regiaoDoCandidato(selecionado)}</Typography>
                            <Typography variant="body2" color="textSecondary">Código de Prova: {selecionado.codigoProva || '--'}</Typography>
                            <Typography variant="body2" color="textSecondary">Código de Inscrição: {selecionado.codigo || '--'}</Typography>
                        </div>
                    </div>
                }

                {etapa === 'selecao' &&
                    <>
                        <Typography variant="body2" color="textSecondary" className={classes.contexto}>
                            Código da Prova: <b>{prova && prova.codigo}</b> — Região de Registro: {regiaoDaProva(prova)}
                        </Typography>

                        <div className={classes.radiosInline}>
                            <Typography variant="body2" className={classes.radiosInlineLabel}>Campo de busca:</Typography>
                            <RadioGroup
                                row
                                aria-label="campoBusca"
                                name="campoBusca"
                                value={campoBusca}
                                onChange={event => setCampoBusca(event.target.value)}
                            >
                                {CAMPOS_BUSCA.map(opcao =>
                                    <FormControlLabel
                                        key={opcao.value}
                                        value={opcao.value}
                                        control={<Radio />}
                                        label={opcao.label}
                                        disabled={carregando}
                                    />
                                )}
                            </RadioGroup>
                        </div>

                        <div className={classes.buscaLinha}>
                            <div className={classes.buscaCampo}>
                                <Text
                                    label={ROTULO_CAMPO[campoBusca]}
                                    value={termo}
                                    onChange={event => setTermo(event.target.value)}
                                    onKeyDown={event => { if (event.key === 'Enter') buscar(); }}
                                    variant="outlined"
                                    disabled={carregando}
                                />
                            </div>
                            <ButtonPrimary
                                name="Buscar"
                                startIcon={<SearchIcon />}
                                onClick={buscar}
                                disabled={!termo.trim() || carregando}
                                style={{ marginTop: 16, height: 56 }}
                            />
                        </div>

                        <CheckBox
                            label="Mostrar somente candidatos confirmados para a mesma região de realização de prova"
                            checked={somenteMesmaRegiao}
                            onChange={event => setSomenteMesmaRegiao(event.target.checked)}
                        />

                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4, textAlign: 'right' }}>
                            Considera apenas candidatos com Inscrição Finalizada.
                        </Typography>

                        <div className={classes.resultados}>
                            {carregando &&
                                <div className={classes.vazio}>
                                    <Typography variant="body2" color="textSecondary">Buscando candidatos...</Typography>
                                </div>
                            }

                            {!carregando && !buscou &&
                                <div className={classes.vazio}>
                                    <Typography variant="body2" color="textSecondary">Informe um termo e clique na lupa para buscar.</Typography>
                                </div>
                            }

                            {!carregando && buscou && resultados.length === 0 &&
                                <div className={classes.vazio}>
                                    <Typography variant="body2" color="textSecondary">Nenhum candidato encontrado.</Typography>
                                </div>
                            }

                            {!carregando && resultados.length > 0 &&
                                <RadioGroup
                                    value={selecionado ? selecionado.id : ''}
                                    onChange={event => setSelecionado(resultados.find(candidato => candidato.id === event.target.value) || null)}
                                >
                                    {resultados.map(candidato => (
                                        <div key={candidato.id} className={classes.resultado}>
                                            <FormControlLabel
                                                value={candidato.id}
                                                control={<Radio color="primary" />}
                                                label={
                                                    <div className={classes.resultadoInfo}>
                                                        <div>{candidato.nomeExibicao}</div>
                                                        <div className={classes.resultadoSecundario}>
                                                            {candidato.nomeSocial && `Nome: ${candidato.nome} · `}
                                                            CPF {candidato.cpf ? cpfMask(candidato.cpf) : '--'} · Região {regiaoDoCandidato(candidato)} · Código da Prova {candidato.codigoProva || '--'} · Inscrição {candidato.codigo || '--'}
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    ))}
                                </RadioGroup>
                            }
                        </div>

                        <div className={classes.rodape}>
                            <Typography variant="caption" color="textSecondary">
                                {totalElementos > 0 ? `Mostrando ${resultados.length} de ${totalElementos} registros` : ' '}
                            </Typography>
                            {temMais &&
                                <ButtonDefault
                                    name="Carregar mais"
                                    startIcon={<RefreshOutlinedIcon />}
                                    onClick={carregarMais}
                                    disabled={carregandoMais}
                                />
                            }
                        </div>
                    </>
                }
            </SimpleDialog>
        </>
    );
};

export default DialogVincularCandidato;
